import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  PlusCircle, ScanLine, Wand2, Send, ArrowRight, ImageOff,
  Car, CheckCircle2, Globe, BadgeCheck, AlertTriangle, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusPill } from "@/components/StatusPill";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SIMPLE_STATUS_META, fmtEUR, fmtDateTime } from "@/lib/constants";

const STEPS = [
  { icon: ScanLine, title: "Fahrzeugdaten laden", text: "FIN eingeben oder scannen - Daten werden automatisch geladen" },
  { icon: Wand2, title: "Bilder mit Fotobooth optimieren", text: "Parkplatzfoto rein, professionelles Studio-Bild raus" },
  { icon: Send, title: "Inserat erstellen & veröffentlichen", text: "KI schreibt das Inserat - Sie prüfen und veröffentlichen" },
];

const nextAction = (v) => {
  if (v.pub_failed) return { label: "Fehler prüfen", to: `/app/fahrzeuge/${v.id}?tab=publish` };
  if (!v.has_photos) return { label: "Fotos hinzufügen", to: `/app/aufnehmen?vehicle=${v.id}&step=2` };
  if (!v.has_price) return { label: "Angaben ergänzen", to: `/app/aufnehmen?vehicle=${v.id}&step=4` };
  if (!v.has_description) return { label: "Inserat erstellen", to: `/app/aufnehmen?vehicle=${v.id}&step=5` };
  if (!v.published && v.simple_status !== "verkauft") return { label: "Veröffentlichen", to: `/app/fahrzeuge/${v.id}?tab=publish` };
  return { label: "Öffnen", to: `/app/fahrzeuge/${v.id}` };
};

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data)
    return (
      <div className="space-y-4">
        <Skeleton className="h-44" />
        <Skeleton className="h-24" />
        <Skeleton className="h-64" />
      </div>
    );

  const k = data.kpis;
  const KPIS = [
    { key: "in_bearbeitung", label: "In Bearbeitung", value: k.in_bearbeitung, icon: Car },
    { key: "bereit", label: "Veröffentlichungsbereit", value: k.bereit, icon: CheckCircle2 },
    { key: "online", label: "Online", value: k.online, icon: Globe },
    { key: "verkauft", label: "Verkauft", value: k.verkauft, icon: BadgeCheck },
    { key: "pub_fehler", label: "Veröffentlichungsfehler", value: k.pub_fehler, icon: AlertTriangle, warn: k.pub_fehler > 0 },
    { key: "content_entwuerfe", label: "Content-Entwürfe", value: k.content_entwuerfe, icon: Sparkles },
  ];

  return (
    <div className="space-y-6 pb-10">
      {/* Grosser CTA */}
      <Card className="relative overflow-hidden border-primary/25" data-testid="dashboard-cta-card">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(600px 220px at 15% 0%, rgba(56,189,248,0.12), transparent 70%)" }} />
        <CardContent className="relative pt-6 pb-6 space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-[-0.02em]">
                Guten Tag{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
              </h1>
              <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                FIN eingeben, Fotos aufnehmen und in wenigen Minuten veröffentlichungsbereit sein.
              </p>
            </div>
            <Button size="lg" onClick={() => navigate("/app/aufnehmen")} className="gap-2 font-semibold h-12 px-6" data-tour="dashboard-cta" data-testid="dashboard-new-vehicle-publish-button">
              <PlusCircle className="h-5 w-5" /> Neues Fahrzeug veröffentlichen
            </Button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3" data-tour="dashboard-steps">
            {STEPS.map((s, i) => (
              <div key={i} className="flex items-start gap-3 rounded-xl border border-border bg-secondary/30 p-3.5" data-testid={`dashboard-step-${i + 1}`}>
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 border border-primary/30">
                  <s.icon className="h-4.5 w-4.5 text-primary" />
                </div>
                <div>
                  <div className="text-sm font-semibold">{i + 1}. {s.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{s.text}</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {KPIS.map((kpi) => (
          <Card key={kpi.key} className={kpi.warn ? "border-red-500/40" : ""} data-testid={`dashboard-kpi-${kpi.key}`}>
            <CardContent className="pt-4 pb-4">
              <div className={`font-display text-2xl font-bold tabular-nums ${kpi.warn ? "text-red-300" : ""}`}>{kpi.value}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{kpi.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Zuletzt bearbeitet */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg font-bold">Zuletzt bearbeitete Fahrzeuge</h2>
          <Link to="/app/fahrzeuge" className="text-xs text-primary flex items-center gap-1" data-testid="dashboard-all-vehicles-link">
            Alle Fahrzeuge <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        {data.zuletzt_bearbeitet.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center space-y-3" data-testid="dashboard-empty-state">
              <Car className="h-8 w-8 text-muted-foreground mx-auto" />
              <p className="text-sm text-muted-foreground">Noch keine Fahrzeuge. Starten Sie mit Ihrem ersten Fahrzeug.</p>
              <Button onClick={() => navigate("/app/aufnehmen")} className="gap-2" data-testid="dashboard-empty-cta">
                <PlusCircle className="h-4 w-4" /> Fahrzeug hinzufügen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dashboard-recent-vehicles">
            {data.zuletzt_bearbeitet.map((v) => {
              const action = nextAction(v);
              const meta = SIMPLE_STATUS_META[v.simple_status] || SIMPLE_STATUS_META.entwurf;
              return (
                <Card key={v.id} className="overflow-hidden card-hover" data-testid={`dashboard-recent-${v.bestandsnummer}`}>
                  <div className="flex">
                    {v.cover_image_url ? (
                      <img src={v.cover_image_url} alt="" className="w-28 h-full min-h-[104px] object-cover cursor-pointer" onClick={() => navigate(`/app/fahrzeuge/${v.id}`)} />
                    ) : (
                      <div className="w-28 min-h-[104px] bg-secondary flex items-center justify-center cursor-pointer" onClick={() => navigate(`/app/fahrzeuge/${v.id}`)}>
                        <ImageOff className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 p-3 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <button className="text-sm font-semibold truncate hover:text-primary text-left" onClick={() => navigate(`/app/fahrzeuge/${v.id}`)} data-testid={`dashboard-recent-title-${v.bestandsnummer}`}>
                          {v.label}
                        </button>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 tabular-nums">
                        {v.price != null ? fmtEUR(v.price) : "Kein Preis"} · {fmtDateTime(v.updated_at)}
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-2">
                        <StatusPill label={meta.label} color={meta.color} />
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => navigate(action.to)} data-testid={`dashboard-recent-action-${v.bestandsnummer}`}>
                          {action.label} <ArrowRight className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
